import { seededRandom } from '@blocksuite/std/inline';

export interface AgentPointerPosition {
  x: number;
  y: number;
}

export const AGENT_POINTER_MIN_MS = 120;
export const AGENT_POINTER_MAX_MS = 600;

/** Duration in screen pixels: 120 ms plus 0.6 ms per pixel, capped at 600 ms. */
export function agentPointerDurationMs(screenPixels: number): number {
  return Math.min(
    AGENT_POINTER_MAX_MS,
    AGENT_POINTER_MIN_MS + Math.max(0, screenPixels) * 0.6
  );
}

export function moveAgentPointer({
  from,
  to,
  view,
  moved,
  arrived,
  pixelsPerUnit,
  seed,
}: {
  from: AgentPointerPosition;
  to: AgentPointerPosition;
  view: Window;
  pixelsPerUnit: number;
  seed: string;
  moved(position: AgentPointerPosition): void;
  /** The pointer reached `to`; not called once stopped. */
  arrived?(): void;
}): () => void {
  const deltaX = to.x - from.x;
  const deltaY = to.y - from.y;
  const distance = Math.hypot(deltaX, deltaY);
  if (
    distance * pixelsPerUnit < 5 ||
    view.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    moved(to);
    arrived?.();
    return () => {};
  }
  const duration = agentPointerDurationMs(distance * pixelsPerUnit);
  const random = seededRandom(seed);
  const perpendicular = { x: -deltaY / distance, y: deltaX / distance };
  const spread = distance * 0.3;
  const firstOffset = (random() - 0.4) * spread;
  const secondOffset = (random() - 0.6) * spread;
  const first = {
    x: from.x + deltaX * 0.25 + perpendicular.x * firstOffset,
    y: from.y + deltaY * 0.25 + perpendicular.y * firstOffset,
  };
  const second = {
    x: from.x + deltaX * 0.75 + perpendicular.x * secondOffset,
    y: from.y + deltaY * 0.75 + perpendicular.y * secondOffset,
  };
  const start = view.performance.now();
  let frame: number;
  let stopped = false;
  const tick = (now: number) => {
    if (stopped) return;
    const progress = Math.min(1, Math.max(0, (now - start) / duration));
    const eased =
      progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
    const remaining = 1 - eased;
    moved({
      x:
        remaining ** 3 * from.x +
        3 * remaining ** 2 * eased * first.x +
        3 * remaining * eased ** 2 * second.x +
        eased ** 3 * to.x,
      y:
        remaining ** 3 * from.y +
        3 * remaining ** 2 * eased * first.y +
        3 * remaining * eased ** 2 * second.y +
        eased ** 3 * to.y,
    });
    if (stopped) return;
    if (progress < 1) frame = view.requestAnimationFrame(tick);
    else arrived?.();
  };
  frame = view.requestAnimationFrame(tick);
  return () => {
    stopped = true;
    view.cancelAnimationFrame(frame);
  };
}
